import {
    createStore,
    applyMiddleware,
    Middleware as ReduxMiddleware,
    compose,
    Store as ReduxStore,
    Dispatch,
    AnyAction,
} from 'redux'
import { TogglesState } from 'common/feature-togglings/reducer'
import { createRootReducer } from 'common/client/reducers'
import { LocationState } from 'common/data/location-detection/reducer'
import { getTrackJSMiddleware, updateTrackJsFeatureMetadata } from './trackjs'
import { NavigationState } from 'common/data/navigation/navigation-open'
import { AuthenticationState, AuthenticationActions } from 'common/data/authentication/reducer'
import { UserPersonalisationState } from 'common/data/user-personalisation/user-personalisation'
import { MetaState } from './meta/meta.redux'
import thunk, { ThunkMiddleware, ThunkDispatch, ThunkAction } from 'redux-thunk'
import { createStateSyncMiddleware } from 'redux-state-sync'
import { ConsentState } from './data/consent/consent.redux'
import { TheWestLiveState } from 'common/data/west-live/reducer'
import { BaseClientConfig } from './client/BaseClientConfig'

export interface AppState {
    toggles: TogglesState
    geoLocation: LocationState
    navigation: NavigationState
    authentication: AuthenticationState
    personalisation: UserPersonalisationState
    meta: MetaState
    consent: ConsentState
    theWestLive: TheWestLiveState
}

export type Middleware = ReduxMiddleware<{}, AppState, Dispatch<AnyAction>>

export interface ExtraThunkArgument {
    config: BaseClientConfig
}

export type Store = ReduxStore<AppState, AnyAction> & {
    dispatch: ThunkDispatch<AppState, ExtraThunkArgument, AnyAction>
}

export type SWMThunkAction<T extends AnyAction> = ThunkAction<void, any, ExtraThunkArgument, T>

const useReduxDevToolsExtension = () =>
    process.env.NODE_ENV !== 'production' &&
    typeof window === 'object' &&
    typeof (window as any).__REDUX_DEVTOOLS_EXTENSION__ !== 'undefined'

// Not a hook just a name clash
// eslint-disable-next-line react-hooks/rules-of-hooks
const devTools = useReduxDevToolsExtension()
    ? (window as any).__REDUX_DEVTOOLS_EXTENSION__()
    : (f: any) => f

export function configureStore(
    config: BaseClientConfig,
    options: {
        initialState?: AppState
        syncState?: boolean
        middlewares?: Middleware[]
    } = {}
): Store {
    const { initialState, syncState, middlewares } = options

    const trackJSMiddleware = getTrackJSMiddleware()

    const stateSyncOptions = {
        whitelist: [AuthenticationActions.LOGGED_IN, AuthenticationActions.LOGGED_OUT],
    }

    let stateSyncMiddleware

    try {
        stateSyncMiddleware = syncState && [createStateSyncMiddleware(stateSyncOptions)]
    } catch {
        stateSyncMiddleware = undefined
    }

    if (initialState) {
        updateTrackJsFeatureMetadata(initialState.toggles)
    }

    const store: Store = createStore(
        createRootReducer(),
        initialState as any,
        compose(
            applyMiddleware(
                ...(middlewares || []),
                ...(stateSyncMiddleware || []),
                trackJSMiddleware,
                thunk.withExtraArgument({ config }) as ThunkMiddleware<
                    AppState,
                    AnyAction,
                    ExtraThunkArgument
                >
            ),
            devTools
        )
    )

    return store
}
